<template>
  <main class="sell-page">
    <div class="wrapper sell-page__container">
      <div class="sell-page__heading">
        <div>
          <div class="sell-page__eyebrow">ROGUERUST MARKETPLACE</div>
          <h1 class="sell-page__title">SELL SKINS</h1>
          <p class="sell-page__subtitle">
            Select items from your Rust inventory and create a sell request.
          </p>
        </div>

        <router-link to="/profile" class="sell-page__back">
          <span class="sell-page__back-arrow">←</span>
          MY PROFILE
        </router-link>
      </div>

      <div class="sell-page__layout">
        <section class="sell-panel">
          <div class="sell-panel__header">
            <div>
              <h2 class="sell-panel__title">YOUR INVENTORY</h2>
              <p class="sell-panel__subtitle">
                Choose the items you want to sell.
              </p>
            </div>

            <div class="sell-panel__count">{{ inventory.length }} ITEMS</div>
          </div>

          <div v-if="waitingForAccess" class="sell-state">
            <div class="sell-spinner"></div>
            <span>Checking your account...</span>
          </div>

          <div v-else-if="loading" class="sell-state">
            <div class="sell-spinner"></div>
            <span>Loading your Rust inventory...</span>
          </div>

          <div v-else-if="error" class="sell-state sell-state--error">
            <div class="sell-state__icon">!</div>
            <h3>INVENTORY UNAVAILABLE</h3>
            <p>{{ error }}</p>

            <button
              type="button"
              class="sell-action sell-action--outline"
              @click="fetchInventory"
            >
              TRY AGAIN
            </button>
          </div>

          <div v-else-if="!inventory.length" class="sell-state">
            <div class="sell-state__icon">0</div>
            <h3>NO ITEMS AVAILABLE</h3>
            <p>
              You don't currently have any inventory items available to sell.
            </p>

            <button
              type="button"
              class="sell-action sell-action--outline"
              @click="fetchInventory"
            >
              REFRESH INVENTORY
            </button>
          </div>

          <template v-else>
            <div class="sell-table-heading">
              <span>ITEM</span>
              <span>AVAILABLE</span>
              <span>QUANTITY</span>
              <span>PRICE / ITEM</span>
            </div>

            <div class="sell-inventory">
              <div
                v-for="(item, index) in inventory"
                :key="item.short_name + '-' + index"
                class="sell-item"
                :class="{ 'sell-item--selected': item.quantity > 0 }"
              >
                <div class="sell-item__identity">
                  <div class="sell-item__icon">
                    <span>{{ item.quantity > 0 ? '✓' : '◆' }}</span>
                  </div>

                  <div class="sell-item__details">
                    <div class="sell-item__name">
                      {{
                        item.game_name ||
                        (item.catalog_item && item.catalog_item.name) ||
                        item.short_name
                      }}
                    </div>

                    <div class="sell-item__short-name">
                      {{ item.short_name }}
                    </div>
                  </div>
                </div>

                <div class="sell-item__available">
                  <span class="sell-item__mobile-label">AVAILABLE</span>
                  <span class="sell-item__available-value">
                    {{ item.amount }}
                    <small>IN STOCK</small>
                  </span>
                </div>

                <div class="sell-item__quantity">
                  <span class="sell-item__mobile-label">QUANTITY</span>

                  <div class="sell-quantity">
                    <button
                      type="button"
                      class="sell-quantity__button"
                      :disabled="submitting || item.quantity <= 0"
                      @click="changeQuantity(item, -1)"
                    >
                      −
                    </button>

                    <input
                      type="number"
                      class="sell-quantity__input"
                      min="0"
                      step="1"
                      :max="item.amount"
                      :value="item.quantity"
                      :disabled="submitting"
                      @input="setQuantity(item, $event.target)"
                    />

                    <button
                      type="button"
                      class="sell-quantity__button"
                      :disabled="submitting || item.quantity >= item.amount"
                      @click="changeQuantity(item, 1)"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div class="sell-item__price">
                  <span class="sell-item__mobile-label">PRICE / ITEM</span>

                  <div class="sell-price">
                    <input
                      v-model.number="item.price"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      :disabled="submitting || item.quantity === 0"
                      :aria-label="'Price for ' + item.short_name"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </section>

        <aside class="sell-summary">
          <div class="sell-summary__header">
            <h2>SELL SUMMARY</h2>
            <p>Review your selected items before submitting.</p>
          </div>

          <div class="sell-summary__stats">
            <div class="sell-summary__stat">
              <span>SELECTED ITEMS</span>
              <strong>{{ selectedItems.length }}</strong>
            </div>

            <div class="sell-summary__stat">
              <span>TOTAL QUANTITY</span>
              <strong>{{ selectedQuantity }}</strong>
            </div>
          </div>

          <div class="sell-summary__divider"></div>

          <div class="sell-summary__total-label">ESTIMATED TOTAL</div>

          <div class="sell-summary__total">
            {{ total.toFixed(2) }}
            <span>COINS</span>
          </div>

          <p class="sell-summary__hint">
            Your request will be reviewed before approval.
          </p>

          <label class="sell-approval">
            <input
              v-model="adminApproval"
              type="checkbox"
              :disabled="submitting"
            />

            <span class="sell-approval__box"></span>

            <span class="sell-approval__text">
              I understand this transaction requires admin approval
            </span>
          </label>

          <div v-if="submitError" class="sell-alert sell-alert--error">
            {{ submitError }}
          </div>

          <div v-if="success" class="sell-alert sell-alert--success">
            Sell request created successfully.
          </div>

          <button
            type="button"
            class="sell-action sell-action--primary"
            :disabled="!canSubmit || submitting"
            @click="submit"
          >
            {{ submitting ? 'CREATING REQUEST...' : 'CREATE SELL REQUEST' }}
          </button>

          <p class="sell-summary__footer">
            Only items available in your inventory can be submitted.
          </p>
        </aside>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'SellSkins',

  data() {
    return {
      inventory: [],
      loading: false,
      submitting: false,
      error: '',
      submitError: '',
      success: false,
      adminApproval: false,
      inventoryLoaded: false,
      accessChecking: true,
      accessStarted: false,
    }
  },

  computed: {
    waitingForAccess() {
      return this.accessChecking
    },

    selectedItems() {
      return this.inventory.filter((item) => item.quantity > 0)
    },

    selectedQuantity() {
      return this.selectedItems.reduce((sum, item) => sum + item.quantity, 0)
    },

    total() {
      return this.selectedItems.reduce(
        (sum, item) => sum + item.quantity * Number(item.price || 0),
        0,
      )
    },

    canSubmit() {
      return (
        !this.waitingForAccess &&
        !this.loading &&
        this.adminApproval &&
        this.selectedItems.length > 0 &&
        this.selectedItems.every(
          (item) =>
            Number.isInteger(item.quantity) &&
            item.quantity > 0 &&
            item.quantity <= item.amount &&
            Number(item.price) > 0 &&
            Number.isFinite(Number(item.price)),
        )
      )
    },
  },

  watch: {
    '$parent.authLoaded'() {
      this.waitForAccess()
    },

    '$parent.settingsLoaded'() {
      this.waitForAccess()
    },
  },

  mounted() {
    this.waitForAccess()
  },

  methods: {
    async waitForAccess() {
      const app = this.$parent

      if (!app.authLoaded || !app.settingsLoaded) {
        this.accessChecking = true
        return
      }

      if (this.accessStarted) {
        return
      }

      this.accessStarted = true
      this.accessChecking = false

      if (!app.isAuth) {
        this.$router.replace('/')
        return
      }

      if (!app.canSellSkins) {
        app.openComingSoonModal()
        this.$router.replace('/profile')
        return
      }

      await this.fetchInventory()
    },

    async fetchInventory() {
      if (this.loading) {
        return
      }

      this.loading = true
      this.error = ''
      this.submitError = ''

      try {
        const response = await this.$http.get(
          process.env.VUE_APP_API + 'user/inventory',
        )

        const payload = response.data && response.data.payload

        if (response.data.status !== 'OK' || !Array.isArray(payload)) {
          throw new Error('Could not load inventory')
        }

        this.inventory = payload
          .filter((item) => Number(item.amount) > 0 && item.short_name)
          .map((item) => ({
            ...item,
            amount: Math.floor(Number(item.amount)),
            quantity: 0,
            price: Number((item.catalog_item && item.catalog_item.price) || 0),
          }))

        this.inventoryLoaded = true
      } catch (err) {
        const status = err.response && err.response.status

        this.error =
          status === 404
            ? 'Join the game server first to load your inventory.'
            : status === 503
              ? 'Game server is currently unavailable.'
              : status === 401
                ? 'Please sign in.'
                : (err.response &&
                    err.response.data &&
                    err.response.data.message) ||
                  err.message ||
                  'Failed to load inventory.'
      } finally {
        this.loading = false
      }
    },

    changeQuantity(item, delta) {
      item.quantity = Math.max(0, Math.min(item.amount, item.quantity + delta))
    },

    setQuantity(item, input) {
      const number = Number(input.value)

      item.quantity = Number.isFinite(number)
        ? Math.max(0, Math.min(item.amount, Math.floor(number)))
        : 0

      input.value = String(item.quantity)
    },

    async submit() {
      if (this.submitting || !this.canSubmit) {
        return
      }

      const app = this.$parent

      if (!app.canSellSkins) {
        app.openComingSoonModal()
        return
      }

      this.submitting = true
      this.submitError = ''
      this.success = false

      try {
        const response = await this.$http.post(
          process.env.VUE_APP_API + 'user/sell-requests',
          {
            items: this.selectedItems.map((item) => ({
              short_name: item.short_name,
              quantity: item.quantity,
              price: Number(item.price),
            })),
          },
        )

        if (response.data.status !== 'OK') {
          throw new Error(
            response.data.message || 'Could not create sell request',
          )
        }

        this.success = true
        this.adminApproval = false

        await this.fetchInventory()
      } catch (err) {
        this.submitError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Could not create sell request.'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.sell-page {
  min-height: 70vh;
  padding: 60px 0 110px;
  color: #f4eded;
  font-family: 'Open Sans', Arial, sans-serif;
}

.sell-page__container {
  max-width: 1360px;
  margin: 0 auto;
}

.sell-page__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 34px;
}

.sell-page__eyebrow {
  color: #e15d40;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 18px;
  letter-spacing: 2px;
  margin-bottom: 8px;
}

.sell-page__title {
  margin: 0;
  color: #fff;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: clamp(48px, 6vw, 82px);
  font-weight: 400;
  line-height: 0.95;
  letter-spacing: 1px;
}

.sell-page__subtitle {
  margin: 16px 0 0;
  color: #b7a7a7;
  font-size: 14px;
  line-height: 1.7;
}

.sell-page__back {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 20px;
  border: 1px solid #544141;
  color: #e9dddd;
  text-decoration: none;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 19px;
  letter-spacing: 0.5px;
  transition:
    border-color 0.2s,
    color 0.2s;
}

.sell-page__back:hover {
  border-color: #e15d40;
  color: #e15d40;
}

.sell-page__back-arrow {
  font-family: Arial, sans-serif;
  font-size: 20px;
}

.sell-page__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  align-items: start;
  gap: 24px;
}

.sell-panel,
.sell-summary {
  background: #211919;
  border: 1px solid #483535;
  min-width: 0;
}

.sell-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 28px 30px;
  border-bottom: 1px solid #433232;
}

.sell-panel__title,
.sell-summary__header h2 {
  margin: 0;
  font-family: 'Bebas Neue', Impact, sans-serif;
  color: #fff;
  font-weight: 400;
  font-size: 32px;
  line-height: 1.1;
  letter-spacing: 0.5px;
}

.sell-panel__subtitle,
.sell-summary__header p {
  margin: 8px 0 0;
  color: #a99a9a;
  font-size: 12px;
  line-height: 1.6;
}

.sell-panel__count {
  flex-shrink: 0;
  color: #e15d40;
  border: 1px solid #664032;
  background: rgba(225, 93, 64, 0.08);
  padding: 10px 13px;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.sell-table-heading {
  display: grid;
  grid-template-columns:
    minmax(180px, 2fr)
    minmax(85px, 0.7fr)
    minmax(135px, 1fr)
    minmax(110px, 0.85fr);
  gap: 16px;
  padding: 15px 24px;
  color: #b8a6a6;
  background: #191313;
  border-bottom: 1px solid #403030;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 17px;
  letter-spacing: 0.6px;
}

.sell-inventory {
  padding: 10px;
}

.sell-item {
  display: grid;
  grid-template-columns:
    minmax(180px, 2fr)
    minmax(85px, 0.7fr)
    minmax(135px, 1fr)
    minmax(110px, 0.85fr);
  align-items: center;
  gap: 16px;
  padding: 18px 14px;
  border: 1px solid transparent;
  border-bottom-color: #392b2b;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;
}

.sell-item:last-child {
  border-bottom-color: transparent;
}

.sell-item--selected {
  background: rgba(225, 93, 64, 0.09);
  border-color: rgba(225, 93, 64, 0.4);
  box-shadow: inset 3px 0 #e15d40;
}

.sell-item__identity {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.sell-item__icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  background: #322424;
  border: 1px solid #503a3a;
  color: #e15d40;
  font-size: 20px;
}

.sell-item__details {
  min-width: 0;
}

.sell-item__name {
  color: #f5eeee;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.sell-item__short-name {
  margin-top: 4px;
  color: #8d7c7c;
  font-size: 11px;
  overflow-wrap: anywhere;
}

.sell-item__available-value {
  color: #f2eaea;
  font-size: 18px;
  font-weight: 700;
}

.sell-item__available-value small {
  display: block;
  margin-top: 3px;
  color: #9c8989;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.sell-item__mobile-label {
  display: none;
}

.sell-quantity {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 132px;
  height: 42px;
  border: 1px solid #503b3b;
  background: #151010;
}

.sell-quantity__button {
  width: 35px;
  height: 40px;
  border: 0;
  background: transparent;
  color: #e15d40;
  font-size: 22px;
  cursor: pointer;
  transition: background 0.2s;
}

.sell-quantity__button:hover:not(:disabled) {
  background: rgba(225, 93, 64, 0.15);
}

.sell-quantity__button:disabled {
  color: #685757;
  cursor: not-allowed;
}

.sell-quantity__input {
  width: 55px;
  height: 38px;
  padding: 0;
  border: 0;
  outline: 0;
  color: #fff;
  background: transparent;
  text-align: center;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
  appearance: textfield;
}

.sell-quantity__input::-webkit-inner-spin-button,
.sell-quantity__input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}

.sell-price input {
  width: 100%;
  max-width: 120px;
  height: 42px;
  padding: 0 12px;
  color: #fff;
  background: #151010;
  border: 1px solid #503b3b;
  outline: 0;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 13px;
}

.sell-price input:focus {
  border-color: #e15d40;
}

.sell-price input:disabled {
  opacity: 0.45;
}

.sell-summary {
  padding: 27px 25px;
}

.sell-summary__header {
  margin-bottom: 28px;
}

.sell-summary__stats {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.sell-summary__stat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sell-summary__stat span {
  color: #b6a3a3;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 17px;
  letter-spacing: 0.4px;
}

.sell-summary__stat strong {
  color: #fff;
  font-size: 17px;
}

.sell-summary__divider {
  height: 1px;
  background: #4a3737;
  margin: 25px 0;
}

.sell-summary__total-label {
  color: #a99595;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 18px;
  letter-spacing: 0.6px;
}

.sell-summary__total {
  margin-top: 6px;
  color: #e15d40;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 52px;
  line-height: 1;
}

.sell-summary__total span {
  color: #a58f8f;
  font-size: 19px;
}

.sell-summary__hint {
  margin: 13px 0 26px;
  color: #a99797;
  font-size: 12px;
  line-height: 1.6;
}

.sell-approval {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 23px;
}

.sell-approval input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}

.sell-approval__box {
  position: relative;
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  border: 1px solid #785454;
  background: #151010;
  margin-top: 2px;
}

.sell-approval input:checked + .sell-approval__box {
  background: #e15d40;
  border-color: #e15d40;
}

.sell-approval input:checked + .sell-approval__box::after {
  content: '✓';
  position: absolute;
  left: 3px;
  top: -2px;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
}

.sell-approval input:focus-visible + .sell-approval__box {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.sell-approval__text {
  color: #d6c8c8;
  font-size: 12px;
  line-height: 1.7;
}

.sell-action {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  padding: 0 22px;
  border: 1px solid transparent;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 21px;
  letter-spacing: 0.6px;
  cursor: pointer;
  transition:
    background 0.2s ease,
    opacity 0.2s ease;
}

.sell-action--primary {
  width: 100%;
  color: #fff;
  background: #d94d32;
}

.sell-action--primary:hover:not(:disabled) {
  background: #ed6547;
}

.sell-action--primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.sell-action--outline {
  color: #fff;
  background: transparent;
  border-color: #d94d32;
}

.sell-action--outline:hover {
  background: rgba(217, 77, 50, 0.15);
}

.sell-summary__footer {
  margin: 15px 0 0;
  text-align: center;
  color: #8f7e7e;
  font-size: 11px;
  line-height: 1.6;
}

.sell-state {
  min-height: 270px;
  padding: 45px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  color: #b9aaaa;
  font-size: 13px;
}

.sell-state h3 {
  margin: 0;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 27px;
  font-weight: 400;
  color: #fff;
}

.sell-state p {
  max-width: 420px;
  margin: 0;
  line-height: 1.6;
}

.sell-state__icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #614141;
  color: #e15d40;
  font-family: 'Bebas Neue', Impact, sans-serif;
  font-size: 30px;
}

.sell-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #5c3c3c;
  border-top-color: #e15d40;
  border-radius: 50%;
  animation: sell-spin 0.7s linear infinite;
}

@keyframes sell-spin {
  to {
    transform: rotate(360deg);
  }
}

.sell-alert {
  padding: 12px;
  margin-bottom: 16px;
  font-size: 12px;
  line-height: 1.5;
}

.sell-alert--error {
  background: rgba(255, 80, 80, 0.08);
  border: 1px solid rgba(255, 80, 80, 0.3);
  color: #ff9898;
}

.sell-alert--success {
  background: rgba(80, 200, 120, 0.08);
  border: 1px solid rgba(80, 200, 120, 0.3);
  color: #9be3ac;
}

@media (max-width: 1100px) {
  .sell-page__layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .sell-summary {
    max-width: none;
  }
}

@media (max-width: 760px) {
  .sell-page {
    padding: 35px 0 70px;
  }

  .sell-page__heading {
    flex-direction: column;
    align-items: flex-start;
  }

  .sell-panel__header {
    padding: 20px;
  }

  .sell-table-heading {
    display: none;
  }

  .sell-inventory {
    padding: 10px;
  }

  .sell-item {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 12px;
    padding: 16px;
    border: 1px solid #443333;
    margin-bottom: 10px;
  }

  .sell-item__identity {
    grid-column: 1 / -1;
  }

  .sell-item__available,
  .sell-item__quantity,
  .sell-item__price {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .sell-item__mobile-label {
    display: block;
    color: #aa9393;
    font-family: 'Bebas Neue', Impact, sans-serif;
    font-size: 16px;
  }

  .sell-item__available-value small {
    display: inline;
    margin-left: 5px;
  }
}

@media (max-width: 430px) {
  .sell-page__title {
    font-size: 56px;
  }

  .sell-panel__count {
    font-size: 15px;
  }

  .sell-item {
    grid-template-columns: 1fr;
  }

  .sell-item__identity {
    grid-column: 1;
  }

  .sell-item__available,
  .sell-item__quantity,
  .sell-item__price {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .sell-summary {
    padding: 22px 18px;
  }
}
</style>
